import React from 'react';
import { ExpandableSection, Box, Link, SpaceBetween } from '@amzn/awsui-components-core';
import { SLACK_ICON_URL } from 'src/constants/AppConstants';
import { Avatar, AvatarSize } from '@amzn/stencil-react-components/avatar';
import { EmployeePopoverCard } from './attendees/EmployeePopoverCard';

export const EAHuddleInformation: React.FC = () => {
  return (
    <div className="ea-huddle-information">
      <p>
        Simplifies and accelerates the process of setting up large senior leadership meetings across organizations. Upload a meeting template to
        automatically resolve attendees and generate ready-to-use meeting requests.
      </p>

      <hr />

      <ExpandableSection headerText="❓ How to Use">
        <div>
          <h5>
            <strong>[1] Download Sample Template (optional)</strong>
          </h5>
          <p>Use our Word template as a starting point</p>

          <h5>
            <strong>[2] Upload Meeting Template</strong>
          </h5>
          <p>Upload the completed template w/ list of attendee names for Huddle+ to look up aliases for</p>

          <h5>
            <strong>[3] Review Attendee list in Huddle+</strong>
          </h5>
          <p>Check for non-matches, add/remove attendee, verify required/optional, set meet owner</p>

          <h5>
            <strong>[4] Create Meeting Draft thru Huddle+</strong>
          </h5>
          <p>Verified names will show up in Outlook Online meeting draft</p>

          <h5>
            <strong>[5] Send Email to EAs</strong>
          </h5>
          <p>Trigger email template from Huddle+ that can be sent to EAs to check LT schedule</p>

          <h5>
            <strong>[6] Send Final Meeting Invite</strong>
          </h5>
          <p>Meeting draft from [4] will show up on your laptop&apos;s Outlook as a starting point for meeting scheduling</p>
        </div>
      </ExpandableSection>

      <hr />

      <ExpandableSection headerText="🎯 Smart Attendee Resolution">
        Automatically identifies and populates both required and optional attendees from the meeting template.
      </ExpandableSection>
      <hr />

      <ExpandableSection headerText="🏢 Cross-Org Coordination">
        Designed for large, senior leadership meetings involving multiple orgs and functions.
      </ExpandableSection>
      <hr />

      <ExpandableSection headerText="📧 Automated Communication">
        Drafts availability request emails with one click, reducing back-and-forth.
      </ExpandableSection>
      <hr />

      <ExpandableSection headerText="📅 Invite Generation">
        Creates Outlook meeting invites in standardized format with complete details.
      </ExpandableSection>
      <hr />

      <ExpandableSection headerText="👤 Proxy Attendees">
        Assign a proxy (delegate) to attend on behalf of a principal attendee. When a proxy is added, the principal is automatically set to optional
        and the proxy appears directly below them in the attendee list. Only one proxy per principal is allowed — adding a new one replaces the
        previous. Proxies are labeled in emails and exports so coordinators can see who is representing whom.
      </ExpandableSection>
      <hr />

      <ExpandableSection headerText="⏰ Time Savings">Eliminates 10–15 minutes of manual work per meeting request.</ExpandableSection>
      <hr />

      <ExpandableSection headerText="🤖 LLM-Powered Parsing">
        Scans meeting templates using advanced AI to extract all relevant information.
      </ExpandableSection>

      <hr />

      <SpaceBetween direction="vertical" size="m">
        <Box>
          <Box fontSize="heading-s" fontWeight="bold">
            Additional Support
          </Box>
          <br />
          <SpaceBetween direction="vertical" size="xs">
            <Box>Contact:</Box>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>•</span>
              <EmployeePopoverCard loginName="jaramja" renderWithPortal>
                <Avatar username="jaramja" size={AvatarSize.ExtraSmall} imageAltText="Profile image of Jamie" />
              </EmployeePopoverCard>
              <a href="https://my.slack.com/app_redirect?channel=@jaramja" target="_blank" rel="noopener noreferrer" title="Slack jaramja">
                <img src={SLACK_ICON_URL} alt="Slack" width="14" height="14" />
              </a>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>•</span>
              <EmployeePopoverCard loginName="mkteja" renderWithPortal>
                <Avatar username="mkteja" size={AvatarSize.ExtraSmall} imageAltText="Profile image of Krishna" />
              </EmployeePopoverCard>
              <a href="https://my.slack.com/app_redirect?channel=@mkteja" target="_blank" rel="noopener noreferrer" title="Slack mkteja">
                <img src={SLACK_ICON_URL} alt="Slack" width="14" height="14" />
              </a>
            </div>
            <Box>
              • SIM: {''}
              <Link href="https://t.corp.amazon.com/create/templates/8d963482-2c95-4b8f-bdbd-b9dbc1db0bc0" external>
                Create Support Ticket
              </Link>
            </Box>
            <Box>
              • Slack: {''}
              <Link href="https://amazon.enterprise.slack.com/archives/C0A38R2A42J" external>
                #huddle-plus-interest
              </Link>
            </Box>
          </SpaceBetween>
        </Box>

        <Box>
          <Box fontSize="heading-s" fontWeight="bold">
            Tutorials
          </Box>
          <br />
          <SpaceBetween direction="vertical" size="xs">
            <Box>
              • Video:{' '}
              <Link href="https://broadcast.amazon.com/videos/1745869" external>
                Huddle+ Tutorial
              </Link>
            </Box>
            <Box>
              • Quip:{' '}
              <Link href="https://quip-amazon.com/XoV8A5qalhT9/EA-Huddle-How-to-Use" external>
                Huddle+ - How to Use
              </Link>
            </Box>
            <Box>
              • Wiki:{' '}
              <Link href="https://w.amazon.com/bin/view/DaS-FinTech/EA-Huddle" external>
                DaS-FinTech/Huddle+ Wiki
              </Link>
            </Box>
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </div>
  );
};
